import React, { useEffect, useState } from 'react'
import { X, Sparkles, Loader2, Copy, Check, RefreshCw, MessageCircle } from 'lucide-react'
import { studentAi, planQrUrl, type StudentAIStatus } from '../../services/studentAi'

// Cửa sổ Gói AI học tập (học sinh): quyền lợi, lượt đã dùng, QR chuyển khoản tự kích hoạt.
interface Props {
  open: boolean
  onClose: () => void
  reason?: string                       // vì sao mở (vd "Nhận xét bài làm cần gói AI")
  onActive?: (s: StudentAIStatus) => void
}

const fmtVnd = (n: number) => n.toLocaleString('vi-VN') + 'đ'

const StudentAIPlanModal: React.FC<Props> = ({ open, onClose, reason, onActive }) => {
  const [s, setS] = useState<StudentAIStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [copied, setCopied] = useState(false)

  const load = async () => {
    setLoading(true); setErr('')
    try {
      const st = await studentAi.status()
      setS(st)
      if (st.active) onActive?.(st)
    } catch (e) {
      setErr((e as Error).message || 'Không tải được thông tin gói. Hãy đăng nhập rồi thử lại.')
    } finally { setLoading(false) }
  }

  useEffect(() => { if (open) load() }, [open])  // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null
  const qr = s ? planQrUrl(s) : null
  const copyMemo = async () => {
    if (!s) return
    try { await navigator.clipboard.writeText(s.memo) } catch { window.prompt('Nội dung chuyển khoản:', s.memo) }
    setCopied(true); setTimeout(() => setCopied(false), 1500)
  }
  const row = (label: string, used: number, limit: number) => (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-700">{label}</span>
      <span className="font-bold text-slate-900">{used}/{limit} lượt</span>
    </div>
  )

  return (
    <div className="fixed inset-0 z-[400] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#fdfcf0] w-full max-w-md max-h-[92vh] overflow-y-auto rounded-2xl shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b border-amber-100 bg-white rounded-t-2xl">
          <Sparkles className="w-5 h-5 text-amber-500" />
          <h3 className="flex-1 font-black text-slate-800">Gói AI học tập</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Đóng"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {reason && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">{reason}</p>}

          {loading && !s && <p className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Đang tải…</p>}
          {err && <p className="text-sm text-rose-600">{err}</p>}

          {s && (
            <>
              <div className="rounded-xl bg-white border border-amber-200 p-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-black text-amber-600">{fmtVnd(s.price_vnd)}</span>
                  <span className="text-sm text-slate-600">/ {s.plan_days} ngày</span>
                  {s.active && <span className="ml-auto text-xs font-bold px-2 py-1 rounded-full bg-teal-100 text-teal-700">Còn {s.days_left} ngày</span>}
                </div>
                <ul className="mt-3 space-y-1 text-sm text-slate-700 list-disc pl-5">
                  <li><strong>Nhận xét bài làm bằng AI</strong>: {s.limits.review} lượt/tháng</li>
                  <li><strong>Hỏi AI</strong> giải thích từng câu: {s.limits.ask} lượt/tháng</li>
                </ul>
                <p className="mt-2 text-xs text-slate-500">Làm bài, chấm điểm, xem đáp án và hướng dẫn giải luôn <strong>miễn phí</strong>, không cần gói.</p>
              </div>

              <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-1.5">
                <p className="text-xs font-bold text-slate-500 uppercase">Đã dùng tháng này</p>
                {row('Nhận xét bài làm', s.usage.review, s.limits.review)}
                {row('Hỏi AI', s.usage.ask, s.limits.ask)}
              </div>

              <div className="rounded-xl bg-white border border-teal-200 p-4 text-center space-y-2">
                <p className="text-sm font-bold text-slate-800">{s.active ? 'Gia hạn thêm 30 ngày' : 'Kích hoạt gói'}</p>
                {qr
                  ? <img src={qr} alt="QR chuyển khoản" className="w-52 h-52 mx-auto rounded-lg border" />
                  : <p className="text-sm text-slate-500">Chưa cấu hình tài khoản nhận tiền — vui lòng nhắn Zalo bên dưới.</p>}
                <p className="text-sm text-slate-700">Số tiền: <strong>{fmtVnd(s.price_vnd)}</strong></p>
                <button onClick={copyMemo} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-900 text-sm font-bold hover:bg-amber-200">
                  Nội dung: <span className="font-mono">{s.memo}</span> {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
                <p className="text-xs text-slate-500 leading-relaxed">Giữ <strong>đúng nội dung chuyển khoản</strong>, gói tự kích hoạt sau 1–2 phút. Chuyển 200.000đ được 60 ngày.</p>
                <div className="flex flex-wrap justify-center gap-2 pt-1">
                  <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-bold hover:bg-teal-700 disabled:opacity-50">
                    {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Tôi đã chuyển khoản
                  </button>
                  <a href={`https://zalo.me/${s.zalo}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50">
                    <MessageCircle size={15} /> Zalo hỗ trợ
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default StudentAIPlanModal
